/**
 * The map's filter column.
 *
 * The same filters as the list's toolbar, stacked instead of laid out in a row,
 * and always open rather than hidden behind a "more filters" button. That
 * difference is the point of the screen: on a list you search and then read the
 * rows, on a map you turn a filter on and watch the country change shape, so
 * every control has to be in front of you at once.
 *
 * The controls themselves are shared (`../filterFields`), and so is everything
 * that decides what a filter MEANS - the `AudienceFilter` shape, the debounce
 * buffer, `audienceToParams`, and the server-side filter behind them. Only the
 * arrangement is local.
 */
import { Combobox, DateRangePicker, type ComboboxOption, type DateRangeValue } from "@wa/ui";
import { ArrowLeft, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "../../../../i18n/I18nProvider";
import { COUNTIES, LEGAL_FORMS } from "../../../../lib/ariregister";
import { type AudienceDraft, type AudienceFilter } from "../../../../lib/audienceFilter";
import { Btn } from "../../../ui";
import { FilterField, LicenceSinceField, RangeField } from "../filterFields";

export function MapFilterRail({
  filters,
  draft,
  setDraft,
  patch,
  activityOptions,
  regRange,
  onRegRange,
  activeCount,
  onClear,
}: {
  filters: AudienceFilter;
  draft: AudienceDraft;
  setDraft: (fn: (d: AudienceDraft) => AudienceDraft) => void;
  patch: (change: Partial<AudienceFilter>) => void;
  activityOptions: ComboboxOption[];
  /** Null while nothing is chosen - the picker's own "no range" value. */
  regRange: DateRangeValue | null;
  onRegRange: (v: DateRangeValue | null) => void;
  activeCount: number;
  onClear: () => void;
}) {
  const t = useT();

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-2 px-4 pb-2.5 pt-3.5">
        {/* The way out. In the normal view the shell's nav is still there, but
            in fullscreen there is no shell at all, and the map had no route back
            to the list it was opened from. Navigating away also drops native
            fullscreen on its own: the element unmounts and the browser exits. */}
        <span className="flex min-w-0 items-center gap-2">
          <Link
            to="/ariregister"
            className="crm-cb crm-cb-sm shrink-0"
            title={t("ariregister.map.toList")}
            aria-label={t("ariregister.map.toList")}
          >
            <ArrowLeft className="size-3.5" />
          </Link>
          <span className="crm-lab !mt-0">{t("ariregister.map.filters")}</span>
        </span>
        {activeCount > 0 ? (
          <Btn small onClick={onClear}>
            <X className="size-3.5" />
            {t("ariregister.map.clear", { n: activeCount })}
          </Btn>
        ) : null}
      </div>

      {/* The column scrolls, the map does not. `min-h-0` is load-bearing: a
          flex child's default `min-height: auto` refuses to shrink below its
          content, so without it the rail grows past the pane and takes the
          page's scrollbar with it. */}
      <div className="min-h-0 flex-1 space-y-3.5 overflow-y-auto px-4 pb-5">
        <label className="crm-inp">
          <input
            value={draft.q}
            onChange={(e) => setDraft((d) => ({ ...d, q: e.target.value }))}
            placeholder={t("ariregister.searchPlaceholder")}
          />
        </label>

        <div className="min-w-0">
          <span className="crm-lab">{t("ariregister.filter.county")}</span>
          <FilterField
            className="w-full"
            value={filters.county}
            onChange={(v) => patch({ county: v })}
            label={t("ariregister.filter.county")}
            anyLabel={t("ariregister.filter.countyAny")}
            options={COUNTIES.map((c) => ({ v: c, label: c }))}
          />
        </div>

        <div className="min-w-0">
          <span className="crm-lab">{t("ariregister.filter.activity")}</span>
          <Combobox
            className="crm-fld-combo w-full"
            options={activityOptions}
            value={filters.emtak}
            onChange={(v) => patch({ emtak: v })}
            clearable
            placeholder={t("ariregister.filter.activity")}
            searchPlaceholder={t("ariregister.filter.activitySearch")}
            emptyLabel={t("ariregister.filter.activityEmpty")}
            popoverClassName="w-[22rem]"
          />
        </div>

        <div className="min-w-0">
          <span className="crm-lab">{t("ariregister.filter.website")}</span>
          <FilterField
            className="w-full"
            value={filters.hasWebsite}
            onChange={(v) => patch({ hasWebsite: v })}
            label={t("ariregister.filter.website")}
            anyLabel={t("ariregister.filter.websiteAny")}
            options={[
              { v: "true", label: t("ariregister.filter.websiteYes") },
              { v: "false", label: t("ariregister.filter.websiteNo") },
            ]}
          />
        </div>

        <RangeField
          label={t("ariregister.filter.revenue")}
          min={draft.revenueMin}
          max={draft.revenueMax}
          onMin={(v) => setDraft((d) => ({ ...d, revenueMin: v }))}
          onMax={(v) => setDraft((d) => ({ ...d, revenueMax: v }))}
          minPlaceholder={t("ariregister.filter.revenueMin")}
          maxPlaceholder={t("ariregister.filter.revenueMax")}
          unit="EUR"
        />

        <RangeField
          label={t("ariregister.filter.employees")}
          min={draft.employeesMin}
          max={draft.employeesMax}
          onMin={(v) => setDraft((d) => ({ ...d, employeesMin: v }))}
          onMax={(v) => setDraft((d) => ({ ...d, employeesMax: v }))}
          minPlaceholder={t("ariregister.filter.min")}
          maxPlaceholder={t("ariregister.filter.max")}
        />

        {/* The same bounds the list offers. The map colours its markers by this
            score, so filtering on it is how a county's best prospects are found
            without reading a single row. */}
        <RangeField
          label={t("ariregister.filter.webNeed")}
          min={draft.webNeedMin}
          max={draft.webNeedMax}
          onMin={(v) => setDraft((d) => ({ ...d, webNeedMin: v }))}
          onMax={(v) => setDraft((d) => ({ ...d, webNeedMax: v }))}
          minPlaceholder={t("ariregister.filter.min")}
          maxPlaceholder={t("ariregister.filter.max")}
        />

        <div className="min-w-0">
          <span className="crm-lab">{t("ariregister.filter.status")}</span>
          <FilterField
            className="w-full"
            value={filters.status}
            onChange={(v) => patch({ status: v })}
            label={t("ariregister.filter.status")}
            anyLabel={t("ariregister.filter.statusAny")}
            options={[
              { v: "R", label: t("ariregister.status.R") },
              { v: "L", label: t("ariregister.status.L") },
              { v: "N", label: t("ariregister.status.N") },
            ]}
          />
        </div>

        <div className="min-w-0">
          <span className="crm-lab">{t("ariregister.filter.legalForm")}</span>
          <FilterField
            className="w-full"
            value={filters.legalForm}
            onChange={(v) => patch({ legalForm: v })}
            label={t("ariregister.filter.legalForm")}
            anyLabel={t("ariregister.filter.legalFormAny")}
            options={LEGAL_FORMS.map((f) => ({ v: f, label: f }))}
          />
        </div>

        <div className="min-w-0">
          <span className="crm-lab">{t("ariregister.filter.vat")}</span>
          <FilterField
            className="w-full"
            value={filters.hasVat}
            onChange={(v) => patch({ hasVat: v })}
            label={t("ariregister.filter.vat")}
            anyLabel={t("ariregister.filter.vatAny")}
            options={[
              { v: "true", label: t("ariregister.filter.vatYes") },
              { v: "false", label: t("ariregister.filter.vatNo") },
            ]}
          />
        </div>

        <div className="min-w-0">
          <span className="crm-lab">{t("ariregister.filter.profitable")}</span>
          <FilterField
            className="w-full"
            value={filters.profitable}
            onChange={(v) => patch({ profitable: v })}
            label={t("ariregister.filter.profitable")}
            anyLabel={t("ariregister.filter.profitableAny")}
            options={[
              { v: "true", label: t("ariregister.filter.profitableYes") },
              { v: "false", label: t("ariregister.filter.profitableNo") },
            ]}
          />
        </div>

        {/* Arrears. Same pair as the list rail, same reasoning: one question, so
            one cell, and the floor stands on its own. */}
        <div className="min-w-0">
          <span className="crm-lab">{t("ariregister.filter.debt")}</span>
          <div className="flex min-w-0 items-center gap-1.5">
            <FilterField
              className="min-w-0 flex-1"
              value={filters.hasDebt}
              onChange={(v) => patch({ hasDebt: v })}
              label={t("ariregister.filter.debt")}
              anyLabel={t("ariregister.filter.debtAny")}
              options={[
                { v: "true", label: t("ariregister.filter.hasDebtYes") },
                { v: "false", label: t("ariregister.filter.hasDebtNo") },
              ]}
            />
            <label className="crm-inp w-[7.5rem] min-w-0 shrink-0">
              <input
                type="number"
                min={0}
                inputMode="numeric"
                value={draft.debtMin}
                onChange={(e) => setDraft((d) => ({ ...d, debtMin: e.target.value }))}
                placeholder={t("ariregister.filter.debtMin")}
              />
            </label>
          </div>
        </div>

        {/* Activity licences. The select and the "granted since" date are one
            question again: does this company hold a permit, and did it get one
            recently enough to still be setting itself up. */}
        <div className="min-w-0">
          <span className="crm-lab">{t("ariregister.filter.licence")}</span>
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <FilterField
              className="min-w-0 flex-1"
              value={filters.hasLicence}
              onChange={(v) => patch({ hasLicence: v })}
              label={t("ariregister.filter.licence")}
              anyLabel={t("ariregister.filter.licenceAny")}
              options={[
                { v: "true", label: t("ariregister.filter.hasLicenceYes") },
                { v: "false", label: t("ariregister.filter.hasLicenceNo") },
              ]}
            />
            <LicenceSinceField
              value={filters.licenceSince}
              onChange={(licenceSince) => patch({ licenceSince })}
              label={t("ariregister.filter.licenceSince")}
            />
          </div>
        </div>

        {/* Public procurement wins. The same pair as the licence cell: has the
            company won a public contract, and how recently. The day picker is
            the licence cell's, which is a plain "since" field. */}
        <div className="min-w-0">
          <span className="crm-lab">{t("ariregister.filter.procurement")}</span>
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <FilterField
              className="min-w-0 flex-1"
              value={filters.wonProcurement}
              onChange={(v) => patch({ wonProcurement: v })}
              label={t("ariregister.filter.procurement")}
              anyLabel={t("ariregister.filter.procurementAny")}
              options={[
                { v: "true", label: t("ariregister.filter.wonProcurementYes") },
                { v: "false", label: t("ariregister.filter.wonProcurementNo") },
              ]}
            />
            <LicenceSinceField
              value={filters.procurementSince}
              onChange={(procurementSince) => patch({ procurementSince })}
              label={t("ariregister.filter.procurementSince")}
            />
          </div>
        </div>

        <RangeField
          label={t("ariregister.filter.capital")}
          min={draft.capitalMin}
          max={draft.capitalMax}
          onMin={(v) => setDraft((d) => ({ ...d, capitalMin: v }))}
          onMax={(v) => setDraft((d) => ({ ...d, capitalMax: v }))}
          minPlaceholder={t("ariregister.filter.capitalMin")}
          maxPlaceholder={t("ariregister.filter.capitalMax")}
          unit="EUR"
        />

        <div className="min-w-0">
          <span className="crm-lab">{t("ariregister.filter.registered")}</span>
          <DateRangePicker
            value={regRange}
            onChange={onRegRange}
            locale="et-EE"
            placeholder={t("ariregister.filter.registeredAny")}
            className="crm-fld crm-fld-date w-full"
          />
        </div>

        <div className="min-w-0">
          <span className="crm-lab">{t("ariregister.filter.contact")}</span>
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              aria-pressed={filters.hasEmail}
              onClick={() => patch({ hasEmail: !filters.hasEmail })}
              className={`crm-cb crm-cb-wide crm-cb-sm${filters.hasEmail ? " is-on" : ""}`}
            >
              {t("ariregister.filter.hasEmail")}
            </button>
            <button
              type="button"
              aria-pressed={filters.hasPhone}
              onClick={() => patch({ hasPhone: !filters.hasPhone })}
              className={`crm-cb crm-cb-wide crm-cb-sm${filters.hasPhone ? " is-on" : ""}`}
            >
              {t("ariregister.filter.hasPhone")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
